import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Archive, Plus, Send, Sparkles } from 'lucide-react';
import {
  getProposalReadiness,
  getProposalReadinessLabel,
  type Proposal,
} from '@oneup/shared';
import { proposalsApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/ui';
import {
  ManagementCardGrid,
  ManagementFooterSummary,
  ManagementHeader,
  ManagementLoadingState,
  ManagementPage,
  ManagementTable,
  type ManagementActiveFilter,
} from '@/components/management';
import { MANAGEMENT_PAGES, usePersistentManagementView } from '@/platform/management-pages';
import { ProposalDrawer } from './ProposalDrawer';

const FILTERS = [
  { id: 'draft', label: 'Draft' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'approved', label: 'Approved' },
  { id: 'needs-pricing', label: 'Needs Pricing' },
  { id: 'client-visible', label: 'Client Visible' },
  { id: 'archived', label: 'Archived' },
] as const;

type FilterId = (typeof FILTERS)[number]['id'];

export function ProposalsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { proposalId } = useParams<{ proposalId: string }>();
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const setNotice = useUiStore((state) => state.setNotice);
  const activeFilter = parseFilter(searchParams.get('filter'));
  const search = searchParams.get('search') ?? '';
  const [viewMode, setViewMode] = usePersistentManagementView(
    'proposals',
    searchParams,
    setSearchParams,
  );
  const serverFilter = activeFilter === 'archived' ? 'archived' : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: serverFilter ? ['proposals', 'all', serverFilter] : ['proposals', 'all'],
    queryFn: () => proposalsApi.list({ limit: 500, filter: serverFilter }),
  });

  const proposals = data?.items ?? [];
  const filtered = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    return proposals
      .filter((proposal) => matchesFilter(proposal, activeFilter))
      .filter((proposal) => !lowerSearch || matchesSearch(proposal, lowerSearch))
      .sort(compareProposals);
  }, [activeFilter, proposals, search]);

  const setFilter = useCallback(
    (filter: FilterId) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (params.get('filter') === filter) params.delete('filter');
        else params.set('filter', filter);
        return params;
      });
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set('search', value);
          else params.delete('search');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openProposal = useCallback(
    (id: string) => {
      const qs = searchParams.toString();
      navigate(qs ? `/proposals/${id}?${qs}` : `/proposals/${id}`);
    },
    [navigate, searchParams],
  );

  const generateDocPlaceholder = (proposal: Proposal) => {
    // TODO: Wire this to the proposal document generation automation.
    setNotice(`Generate Doc is not wired yet for ${proposal.proposalName}`);
  };

  const sendPlaceholder = (proposal: Proposal) => {
    // TODO: Wire this to the proposal send/review flow.
    setNotice(`Send Proposal is not wired yet for ${proposal.proposalName}`);
  };

  if (isLoading) {
    return <ManagementLoadingState metricCount={4} />;
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="mb-1 text-base font-semibold text-destructive">Could not load proposals</h2>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </Card>
    );
  }

  const page = MANAGEMENT_PAGES.proposals;
  const activeFilters: ManagementActiveFilter[] = [
    activeFilter
      ? {
          id: 'filter',
          label: 'Filter',
          value: FILTERS.find((filter) => filter.id === activeFilter)?.label,
          onClear: () => setFilter(activeFilter),
        }
      : undefined,
    search
      ? {
          id: 'search',
          label: 'Search',
          value: `"${search}"`,
          onClear: () => setSearch(''),
        }
      : undefined,
  ].filter((item): item is ManagementActiveFilter => Boolean(item));

  return (
    <ManagementPage>
      <ManagementHeader
        title={page.title}
        description={page.description}
        source={page.source}
        search={{ value: search, onChange: setSearch, placeholder: 'Search proposals' }}
        filters={[
          {
            id: 'proposal-filter',
            label: 'Status',
            value: activeFilter,
            onChange: (value) => setFilter(value as FilterId),
            options: FILTERS.map((filter) => ({ value: filter.id, label: filter.label })),
          },
        ]}
        activeFilters={activeFilters}
        primaryAction={{ label: 'Create Proposal', icon: Plus, onClick: () => openCreateDrawer({ type: 'proposal' }) }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {viewMode === 'table' ? (
      <ManagementTable>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
                <th className="px-3 py-2.5">Proposal</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Visibility</th>
                <th className="px-3 py-2.5">Amount</th>
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5">Valid Until</th>
                <th className="px-3 py-2.5">Timeline</th>
                <th className="px-3 py-2.5">Internal Next Action</th>
                <th className="px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-12 text-center text-sm text-muted-foreground">
                    No proposals match this view.
                  </td>
                </tr>
              )}
              {filtered.map((proposal) => {
                const readinessLabel = getProposalReadinessLabel(proposal);
                return (
                  <tr
                    key={proposal.id}
                    onClick={() => openProposal(proposal.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openProposal(proposal.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Open ${proposal.proposalName}`}
                    className={cn(
                      'cursor-pointer border-t border-border transition-colors hover:bg-muted/30',
                      'focus-visible:bg-muted/40 focus-visible:outline-none',
                      proposalId === proposal.id && 'bg-primary/5',
                    )}
                  >
                    <td className="min-w-[260px] px-3 py-2.5">
                      <div className="font-medium text-foreground">{proposal.proposalName}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Badge variant={readinessLabel === 'Needs Pricing' ? 'warning' : 'muted'}>
                          {readinessLabel}
                        </Badge>
                        {getProposalReadiness(proposal).warnings.length > 0 && (
                          <Badge variant="outline">
                            {getProposalReadiness(proposal).warnings.length} warnings
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {proposal.proposalType ?? '-'}
                    </td>
                    <td className="px-3 py-2.5">
                      {proposal.status ? <Badge variant="muted">{proposal.status}</Badge> : '-'}
                    </td>
                    <td className="px-3 py-2.5">
                      {proposal.portalVisibility ? (
                        <Badge
                          variant={
                            proposal.portalVisibility === 'Client Visible' ? 'info' : 'muted'
                          }
                        >
                          {proposal.portalVisibility}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-medium tabular-nums">
                      {formatCurrency(proposal.proposalAmount) ?? '-'}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {formatDate(proposal.proposalDate)}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {formatDate(proposal.validUntil)}
                    </td>
                    <td className="max-w-[180px] px-3 py-2.5 text-muted-foreground">
                      <span className="line-clamp-2">{proposal.timeline ?? '-'}</span>
                    </td>
                    <td className="max-w-[240px] px-3 py-2.5 text-muted-foreground">
                      <span className="line-clamp-2">{proposal.internalNextAction ?? '-'}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex min-w-[230px] flex-wrap gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            openProposal(proposal.id);
                          }}
                        >
                          Review/Open
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            openProposal(proposal.id);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            generateDocPlaceholder(proposal);
                          }}
                        >
                          <Sparkles className="h-3.5 w-3.5" aria-hidden />
                          Generate Doc
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={(event) => {
                            event.stopPropagation();
                            sendPlaceholder(proposal);
                          }}
                        >
                          <Send className="h-3.5 w-3.5" aria-hidden />
                          Send Proposal
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ManagementTable>
      ) : (
        <ProposalCardView
          proposals={filtered}
          activeProposalId={proposalId}
          onOpen={openProposal}
          onGenerateDoc={generateDocPlaceholder}
          onSend={sendPlaceholder}
        />
      )}

      <ManagementFooterSummary>
        Showing <span className="tabular-nums text-foreground">{filtered.length}</span>
        {proposals.length !== filtered.length && (
          <>
            {' '}
            of <span className="tabular-nums">{proposals.length}</span> loaded
          </>
        )}
      </ManagementFooterSummary>

      {proposalId && <ProposalDrawer proposalId={proposalId} />}
    </ManagementPage>
  );
}

function ProposalCardView({
  proposals,
  activeProposalId,
  onOpen,
  onGenerateDoc,
  onSend,
}: {
  proposals: Proposal[];
  activeProposalId: string | undefined;
  onOpen: (id: string) => void;
  onGenerateDoc: (proposal: Proposal) => void;
  onSend: (proposal: Proposal) => void;
}) {
  if (proposals.length === 0) {
    return (
      <Card className="px-3 py-8 text-center text-sm text-muted-foreground">
        No proposals match this view.
      </Card>
    );
  }

  return (
    <ManagementCardGrid>
      {proposals.map((proposal) => {
        const readinessLabel = getProposalReadinessLabel(proposal);
        const warnings = getProposalReadiness(proposal).warnings.length;
        return (
          <Card
            key={proposal.id}
            className={cn(
              'cursor-pointer space-y-3 p-3 transition-colors hover:bg-muted/25',
              activeProposalId === proposal.id && 'bg-primary/5 ring-1 ring-primary/20',
            )}
            onClick={() => onOpen(proposal.id)}
          >
            <div className="space-y-1">
              <div className="line-clamp-2 text-sm font-semibold text-foreground">
                {proposal.proposalName}
              </div>
              <div className="flex flex-wrap gap-1">
                <Badge variant={readinessLabel === 'Needs Pricing' ? 'warning' : 'muted'}>
                  {readinessLabel}
                </Badge>
                {proposal.status && <Badge variant="outline">{proposal.status}</Badge>}
                {warnings > 0 && <Badge variant="outline">{warnings} warnings</Badge>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <ProposalCardFact label="Amount" value={formatCurrency(proposal.proposalAmount) ?? '-'} />
              <ProposalCardFact label="Date" value={formatDate(proposal.proposalDate)} />
              <ProposalCardFact label="Valid" value={formatDate(proposal.validUntil)} />
            </div>
            {proposal.internalNextAction && (
              <p className="line-clamp-2 text-[12px] text-muted-foreground">
                {proposal.internalNextAction}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5" onClick={(event) => event.stopPropagation()}>
              <Button size="sm" variant="outline" onClick={() => onOpen(proposal.id)}>
                Review/Open
              </Button>
              <Button size="sm" variant="outline" onClick={() => onOpen(proposal.id)}>
                Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => onGenerateDoc(proposal)}>
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                Generate Doc
              </Button>
              <Button size="sm" variant="outline" onClick={() => onSend(proposal)}>
                <Send className="h-3.5 w-3.5" aria-hidden />
                Send
              </Button>
            </div>
          </Card>
        );
      })}
    </ManagementCardGrid>
  );
}

function ProposalCardFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-2 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-[12px] font-medium text-foreground">{value}</div>
    </div>
  );
}

function parseFilter(value: string | null): FilterId | undefined {
  return FILTERS.some((filter) => filter.id === value) ? (value as FilterId) : undefined;
}

function matchesFilter(proposal: Proposal, filter: FilterId | undefined): boolean {
  if (!filter) return true;
  if (filter === 'draft') return proposal.status === 'Draft';
  if (filter === 'submitted') return proposal.status === 'Submitted';
  if (filter === 'approved') return proposal.status === 'Approved';
  if (filter === 'needs-pricing') return !proposal.proposalAmount || proposal.proposalAmount <= 0;
  if (filter === 'client-visible') return proposal.portalVisibility === 'Client Visible';
  if (filter === 'archived') return proposal.portalVisibility === 'Archived';
  return true;
}

function matchesSearch(proposal: Proposal, query: string): boolean {
  return [
    proposal.proposalName,
    proposal.scopeSummary,
    proposal.servicesDescription,
    proposal.notes,
    proposal.internalNextAction,
  ].some((value) => value?.toLowerCase().includes(query));
}

function compareProposals(a: Proposal, b: Proposal): number {
  const dateCompare = compareDesc(a.proposalDate, b.proposalDate);
  if (dateCompare !== 0) return dateCompare;
  return compareDesc(a.createdAt, b.createdAt);
}

function compareDesc(a: string | undefined, b: string | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return b.localeCompare(a);
}

function formatCurrency(value: number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatDate(value: string | undefined): string {
  return value ? value.slice(0, 10) : '-';
}
